// ===================================================================
// PA GRANTS — Projects
// Full CRUD with multi-tab form, search/filter, status/phase badges
// ===================================================================

import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import {
  Title, Text, Paper, Group, Stack, Badge, Button, TextInput, Select,
  Textarea, Modal, Tabs, SimpleGrid, Checkbox, ActionIcon, Loader, Center,
  Table, Popover,
} from '@mantine/core';
import { IconPlus, IconEdit, IconTrash, IconSearch, IconRefresh, IconGitMerge, IconExternalLink, IconDownload, IconCopy } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { exportToCSV } from '../../lib/exportUtils';
import { usePAGrantsStore } from '../../store/paGrantsStore';
import { useDisasterFilter } from '../../hooks/useActiveDisaster';
import { useWorkflowStore } from '../../store/workflowStore';
import { useAuthStore } from '../../store/authStore';
import { TABLES, WORKER_URL } from '../../lib/api-switch';
import type { PAProject, DamageCategory, ProjectPhase, ProjectStatus } from '../../types/pagrants';
import {
  PROJECT_PHASE_LABELS,
  PROJECT_STATUS_LABELS,
  DAMAGE_CATEGORY_LABELS,
  DAMAGE_CATEGORY_COLORS,
} from '../../types/pagrants';

// ── Constants ────────────────────────────────────────────────────

const PHASE_COLORS: Record<string, string> = {
  formulation: 'blue', review: 'yellow', obligation: 'green',
  work_in_progress: 'orange', closeout: 'violet', appeal: 'red',
};

const STATUS_COLORS: Record<string, string> = {
  draft: 'gray', in_review: 'yellow', approved: 'green', obligated: 'teal',
  in_progress: 'blue', completed: 'green', closed: 'gray', appealed: 'red',
};

const CATEGORY_OPTIONS: Array<{ value: string; label: string }> = (
  Object.entries(DAMAGE_CATEGORY_LABELS) as Array<[DamageCategory, string]>
).map(([v, l]) => ({ value: v, label: l }));

const PHASE_OPTIONS: Array<{ value: string; label: string }> = (
  Object.entries(PROJECT_PHASE_LABELS) as Array<[ProjectPhase, string]>
).map(([v, l]) => ({ value: v, label: l }));

const STATUS_OPTIONS: Array<{ value: string; label: string }> = (
  Object.entries(PROJECT_STATUS_LABELS) as Array<[ProjectStatus, string]>
).map(([v, l]) => ({ value: v, label: l }));

const SIZE_OPTIONS = [
  { value: 'small', label: 'Small' },
  { value: 'large', label: 'Large' },
];

const WORK_TYPE_OPTIONS = [
  { value: 'emergency_work', label: 'Emergency Work (Cat A/B)' },
  { value: 'permanent_repair', label: 'Permanent Repair' },
  { value: 'permanent_replace', label: 'Permanent Replacement' },
  { value: 'improved_project', label: 'Improved Project (406)' },
  { value: 'alternate_project', label: 'Alternate Project (428)' },
  { value: 'debris_removal', label: 'Debris Removal' },
  { value: 'emergency_protective', label: 'Emergency Protective Measures' },
];

const REPAIR_REPLACE_OPTIONS = [
  { value: 'repair', label: 'Repair' },
  { value: 'replace', label: 'Replace' },
];

const YES_NO_OPTIONS = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

const FILTER_CATEGORY_OPTIONS = [
  { value: 'all', label: 'All Categories' },
  ...CATEGORY_OPTIONS,
];

const FILTER_PHASE_OPTIONS = [
  { value: 'all', label: 'All Phases' },
  ...PHASE_OPTIONS,
];

const FILTER_STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  ...STATUS_OPTIONS,
];

// ── Form state ──────────────────────────────────────────────────

type FormState = Omit<PAProject, 'id' | 'Rid' | 'creatorId' | 'createdDate' | 'lastModifiedBy' | 'lastModifiedDate' | 'versionNumber' | 'femaProjectId'>;

function emptyForm(): FormState {
  return {
    organizationId: '', disasterId: '',
    projectNumber: '', projectTitle: '',
    category: '', projectSize: '', workType: '',
    projectAddress: '', projectCity: '', projectCounty: '',
    projectState: '', projectZip: '', projectLat: '', projectLng: '',
    damageDescription: '', scopeOfWork: '', costNarrative: '',
    estimatedCost: '', actualCost: '',
    federalSharePct: '75', federalShareAmount: '',
    applicantShareAmount: '', insuranceDeduction: '',
    mitigationCost: '', netEligibleCost: '', obligatedAmount: '',
    drawdownAmount: '', pendingReimbursement: '',
    hazardMitigationIncluded: 'no', hazardMitigationCost: '',
    hazardMitigationDescription: '', hazardMitigationBca: '',
    repairOrReplace: '', improvedProjectElection: 'no',
    improvedProjectDescription: '',
    preApprovalRequired: 'no', preApprovalStatus: '',
    completionPctEstimate: '', completionDate: '',
    workStartDate: '', deadline: '',
    extensionRequested: 'no', extensionNewDeadline: '',
    extensionJustification: '',
    phase: 'formulation', status: 'draft',
    assignedSpecialist: '',
    cefRequired: 'no',
    certLegalResponsibility: '', certInsuranceCompliance: '',
    certProcurementCompliance: '', certNoWorkDuplication: '',
    certSignedBy: '', certSignedDate: '',
    notes: '',
  };
}

function formFromProject(p: PAProject): FormState {
  const {
    id: _id, Rid: _rid, creatorId: _c, createdDate: _cd,
    lastModifiedBy: _lm, lastModifiedDate: _lmd,
    versionNumber: _vn, femaProjectId: _fp,
    ...rest
  } = p;
  return rest;
}

// ── Helpers ─────────────────────────────────────────────────────

const fmt = (v: string) => {
  const n = parseFloat(v);
  return isNaN(n) ? '--' : '$' + n.toLocaleString('en-US', { maximumFractionDigits: 0 });
};

/** Format a raw number string as $1,234.56 while typing */
function formatCurrency(raw: string): string {
  // Strip everything except digits and decimal point
  const cleaned = raw.replace(/[^0-9.]/g, '');
  if (!cleaned) return '';
  const parts = cleaned.split('.');
  const whole = parts[0].replace(/^0+(?=\d)/, '');
  const formatted = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  if (parts.length > 1) {
    return `$${formatted}.${parts[1].slice(0, 2)}`;
  }
  return formatted ? `$${formatted}` : '';
}

/** Strip formatting back to raw number for storage */
function stripCurrency(formatted: string): string {
  return formatted.replace(/[$,]/g, '');
}

// ── HERE Autosuggest for address autocomplete ───────────────
const WORKER = WORKER_URL;
const HEADERS = { 'Content-Type': 'application/json', Origin: 'https://apps.opengov.com' };

interface HereSuggestion {
  title: string;
  address?: {
    label?: string;
    street?: string;
    city?: string;
    county?: string;
    state?: string;
    postalCode?: string;
  };
  position?: { lat: number; lng: number };
}

/** Generate next PW number from existing projects */
function nextProjectNumber(projects: PAProject[]): string {
  let max = 0;
  for (const p of projects) {
    const m = p.projectNumber.match(/PW-?(\d+)/i);
    if (m) max = Math.max(max, parseInt(m[1], 10));
  }
  return `PW-${String(max + 1).padStart(3, '0')}`;
}

// ── Component ───────────────────────────────────────────────────

export default function Projects() {
  const {
    projects: allProjects, organizations, disasters,
    loadingProjects,
    fetchAll, fetchProjects,
    createProject, updateProject, deleteProject,
  } = usePAGrantsStore();
  const projects = useDisasterFilter(allProjects).filter(p => p.workType !== 'hmgp');

  const navigate = useNavigate();

  // Filters
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('all');
  const [phaseFilter, setPhaseFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortField, setSortField] = useState<string>('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const toggleSort = (field: string) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };
  const sortIcon = (f: string) => sortField === f ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<PAProject | null>(null);
  const [deletingProject, setDeletingProject] = useState<PAProject | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState<string | null>('basic');

  // Address autocomplete
  const [addrSuggestions, setAddrSuggestions] = useState<HereSuggestion[]>([]);
  const [addrOpen, setAddrOpen] = useState(false);
  const addrTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchAddressSuggestions = useCallback((query: string) => {
    if (addrTimerRef.current) clearTimeout(addrTimerRef.current);
    if (query.length < 3) { setAddrSuggestions([]); setAddrOpen(false); return; }
    addrTimerRef.current = setTimeout(async () => {
      try {
        const res = await fetch(
          `${WORKER}/api/here/autosuggest?q=${encodeURIComponent(query)}&at=33.749,-84.388&limit=5`,
          { headers: HEADERS },
        );
        const data = await res.json() as { items?: HereSuggestion[] };
        const items = (data.items ?? []).filter(i => i.address);
        setAddrSuggestions(items);
        setAddrOpen(items.length > 0);
      } catch { /* ignore */ }
    }, 300);
  }, []);

  const pickAddress = (s: HereSuggestion) => {
    setForm(prev => ({
      ...prev,
      projectAddress: s.address?.street || s.address?.label || s.title,
      projectCity: s.address?.city || '',
      projectCounty: s.address?.county || '',
      projectState: s.address?.state || '',
      projectZip: s.address?.postalCode || '',
      projectLat: s.position ? String(s.position.lat) : '',
      projectLng: s.position ? String(s.position.lng) : '',
    }));
    setAddrOpen(false);
    setAddrSuggestions([]);
  };

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Lookup maps
  const orgMap = useMemo(() => {
    const m = new Map<string, string>();
    organizations.forEach(o => m.set(String(o.Rid), o.orgName));
    return m;
  }, [organizations]);

  const disasterMap = useMemo(() => {
    const m = new Map<string, string>();
    disasters.forEach(d => m.set(String(d.Rid), d.disasterName || d.disasterNumber));
    return m;
  }, [disasters]);

  const orgOptions = useMemo(() =>
    organizations.map(o => ({ value: String(o.Rid), label: o.orgName || `Org #${o.Rid}` })),
    [organizations],
  );

  const disasterOptions = useMemo(() =>
    disasters.map(d => ({ value: String(d.Rid), label: d.disasterName || d.disasterNumber || `Disaster #${d.Rid}` })),
    [disasters],
  );

  // Filtered list
  const filtered = useMemo(() => {
    let list = projects;
    if (catFilter !== 'all') list = list.filter(p => p.category === catFilter);
    if (phaseFilter !== 'all') list = list.filter(p => p.phase === phaseFilter);
    if (statusFilter !== 'all') list = list.filter(p => p.status === statusFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(p =>
        (p.projectNumber || '').toLowerCase().includes(q) ||
        (p.projectTitle || '').toLowerCase().includes(q) ||
        (p.assignedSpecialist || '').toLowerCase().includes(q) ||
        (orgMap.get(p.organizationId) ?? '').toLowerCase().includes(q) ||
        (disasterMap.get(p.disasterId) ?? '').toLowerCase().includes(q),
      );
    }
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      if (!sortField) return a.projectNumber.localeCompare(b.projectNumber);
      const va = (a as any)[sortField] ?? '';
      const vb = (b as any)[sortField] ?? '';
      const numA = Number(va); const numB = Number(vb);
      if (!isNaN(numA) && !isNaN(numB) && va !== '' && vb !== '') return (numA - numB) * dir;
      return String(va).localeCompare(String(vb)) * dir;
    });
  }, [projects, catFilter, phaseFilter, statusFilter, search, orgMap, disasterMap, sortField, sortDir]);

  // Form helpers
  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm(prev => ({ ...prev, [key]: value }));
  }

  function openCreate() {
    setEditingProject(null);
    const f = emptyForm();
    f.projectNumber = nextProjectNumber(projects);
    setForm(f);
    setActiveTab('basic');
    setModalOpen(true);
  }

  function openEdit(p: PAProject) {
    setEditingProject(p);
    setForm(formFromProject(p));
    setActiveTab('basic');
    setModalOpen(true);
  }

  function openDuplicate(p: PAProject) {
    setEditingProject(null);
    const dup = formFromProject(p);
    dup.projectTitle = `${p.projectTitle} (Copy)`;
    dup.projectNumber = nextProjectNumber(projects);
    dup.status = 'draft';
    dup.phase = 'formulation';
    setForm(dup);
    setActiveTab('basic');
    setModalOpen(true);
  }

  function openDelete(p: PAProject) {
    setDeletingProject(p);
    setDeleteOpen(true);
  }

  async function handleSave() {
    if (!form.projectTitle?.trim()) {
      notifications.show({ message: 'Project title is required', color: 'yellow' });
      return;
    }
    setSaving(true);
    try {
      if (editingProject) {
        await updateProject(editingProject.Rid, form);
        notifications.show({ message: 'Project updated', color: 'green' });
      } else {
        const newRid = await createProject(form);
        notifications.show({ message: 'Project created', color: 'green' });

        // Auto-start PA workflow if a matching template exists
        await tryAutoStartWorkflow(newRid, form.projectTitle ?? '', form.projectNumber ?? '');
      }
      setModalOpen(false);
    } catch (err: unknown) {
      notifications.show({
        message: err instanceof Error ? err.message : 'Save failed',
        color: 'red',
      });
    } finally {
      setSaving(false);
    }
  }

  async function tryAutoStartWorkflow(projectRid: number, title: string, projectNum: string) {
    try {
      const { templates, loadTemplates, startWorkflow } = useWorkflowStore.getState();
      const { user } = useAuthStore.getState();
      if (!user) return;

      // Load templates if not already loaded
      if (templates.length === 0) await loadTemplates();
      const allTemplates = useWorkflowStore.getState().templates;

      // Find an active PA Grants workflow template
      const paTemplate = allTemplates.find(
        t => (t.active === 'yes' || t.active === 'Yes') && t.module === 'pagrants'
      );
      if (!paTemplate) return;

      const label = projectNum ? `${projectNum} — ${title}` : title;
      await startWorkflow(
        String(paTemplate.Rid),
        'pagrants',
        TABLES.paProjects,
        String(projectRid),
        label,
        user.email,
      );
      notifications.show({
        title: 'Workflow Started',
        message: `"${paTemplate.name}" workflow started for ${label}`,
        color: 'violet',
        icon: <IconGitMerge size={16} />,
      });
    } catch (err) {
      console.error('[Workflow auto-start failed]', err);
      // Don't fail the project creation if workflow start fails
    }
  }

  async function handleDelete() {
    if (!deletingProject) return;
    setDeleting(true);
    try {
      await deleteProject(deletingProject.Rid);
      notifications.show({ message: `Project "${deletingProject.projectTitle}" deleted`, color: 'gray' });
      setDeleteOpen(false);
      setDeletingProject(null);
    } catch (err: unknown) {
      notifications.show({
        message: err instanceof Error ? err.message : 'Delete failed',
        color: 'red',
      });
    } finally {
      setDeleting(false);
    }
  }

  // Loading
  if (loadingProjects && projects.length === 0) {
    return <Center h={300}><Loader size="lg" /></Center>;
  }

  return (
    <Stack gap="md">
      {/* Header */}
      <Group justify="space-between">
        <Title order={3} c="black">Projects</Title>
        <Group gap="xs">
          <Button size="xs" variant="light" leftSection={<IconDownload size={16} />} onClick={() => {
            exportToCSV('pa-projects.csv',
              ['Project #', 'Title', 'Applicant', 'Category', 'Size', 'Phase', 'Status', 'Estimated Cost', 'Obligated'],
              filtered.map(p => [p.projectNumber, p.projectTitle, orgMap.get(p.organizationId) || '', p.category, p.projectSize, p.phase, p.status, p.estimatedCost, p.obligatedAmount])
            );
          }}>Export CSV</Button>
          <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />}
            loading={loadingProjects} onClick={() => fetchProjects()}>Refresh</Button>
          <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>
            Add Project
          </Button>
        </Group>
      </Group>

      {/* Filters */}
      <Group gap="sm">
        <TextInput
          placeholder="Search project #, title, specialist, org, disaster..."
          leftSection={<IconSearch size={14} />}
          value={search}
          onChange={e => setSearch(e.currentTarget.value)}
          size="xs"
          style={{ flex: 1, minWidth: 220 }}
        />
        <Select data={FILTER_CATEGORY_OPTIONS} value={catFilter}
          onChange={v => setCatFilter(v ?? 'all')} size="xs" w={220} />
        <Select data={FILTER_PHASE_OPTIONS} value={phaseFilter}
          onChange={v => setPhaseFilter(v ?? 'all')} size="xs" w={180} />
        <Select data={FILTER_STATUS_OPTIONS} value={statusFilter}
          onChange={v => setStatusFilter(v ?? 'all')} size="xs" w={170} />
      </Group>

      <Text size="xs" c="dimmed">
        {filtered.length} project{filtered.length !== 1 ? 's' : ''}
      </Text>

      {/* Table */}
      <Paper withBorder>
        <div style={{ overflowX: 'auto' }}>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ cursor: 'pointer' }} onClick={() => toggleSort('projectNumber')}>Project #{sortIcon('projectNumber')}</Table.Th>
                <Table.Th style={{ cursor: 'pointer' }} onClick={() => toggleSort('projectTitle')}>Title{sortIcon('projectTitle')}</Table.Th>
                <Table.Th style={{ cursor: 'pointer' }} onClick={() => toggleSort('category')}>Category{sortIcon('category')}</Table.Th>
                <Table.Th style={{ cursor: 'pointer' }} onClick={() => toggleSort('projectSize')}>Size{sortIcon('projectSize')}</Table.Th>
                <Table.Th style={{ cursor: 'pointer' }} onClick={() => toggleSort('phase')}>Phase{sortIcon('phase')}</Table.Th>
                <Table.Th style={{ cursor: 'pointer' }} onClick={() => toggleSort('status')}>Status{sortIcon('status')}</Table.Th>
                <Table.Th style={{ textAlign: 'right', cursor: 'pointer' }} onClick={() => toggleSort('estimatedCost')}>Est. Cost{sortIcon('estimatedCost')}</Table.Th>
                <Table.Th>Organization</Table.Th>
                <Table.Th>Disaster</Table.Th>
                <Table.Th style={{ textAlign: 'right' }}>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {filtered.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={10} style={{ textAlign: 'center', padding: 24, color: '#888' }}>
                    {loadingProjects ? 'Loading...' : 'No projects found'}
                  </Table.Td>
                </Table.Tr>
              )}
              {filtered.map(p => (
                <Table.Tr key={p.id}
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/pagrants/pa/applicants/${p.organizationId}/projects/${p.Rid}`)}>
                  <Table.Td fw={500}>{p.projectNumber || '--'}</Table.Td>
                  <Table.Td>{p.projectTitle || '--'}</Table.Td>
                  <Table.Td>
                    {p.category ? (
                      <Badge size="sm" color={DAMAGE_CATEGORY_COLORS[p.category as DamageCategory] ?? 'gray'}>
                        {DAMAGE_CATEGORY_LABELS[p.category as DamageCategory] ?? p.category}
                      </Badge>
                    ) : '--'}
                  </Table.Td>
                  <Table.Td>
                    {p.projectSize ? (
                      <Badge size="sm" variant="outline"
                        color={p.projectSize === 'large' ? 'orange' : 'blue'}>
                        {p.projectSize}
                      </Badge>
                    ) : '--'}
                  </Table.Td>
                  <Table.Td>
                    <Badge size="sm" color={PHASE_COLORS[p.phase] ?? 'gray'}>
                      {PROJECT_PHASE_LABELS[p.phase as ProjectPhase] ?? p.phase}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Badge size="sm" color={STATUS_COLORS[p.status] ?? 'gray'}>
                      {PROJECT_STATUS_LABELS[p.status as ProjectStatus] ?? p.status}
                    </Badge>
                  </Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{fmt(p.estimatedCost)}</Table.Td>
                  <Table.Td>{orgMap.get(p.organizationId) || '--'}</Table.Td>
                  <Table.Td>{disasterMap.get(p.disasterId) || '--'}</Table.Td>
                  <Table.Td>
                    <Group gap={4} wrap="nowrap" justify="flex-end">
                      <ActionIcon size="sm" variant="subtle" color="teal" title="Open project"
                        onClick={(e) => { e.stopPropagation(); navigate(`/pagrants/pa/applicants/${p.organizationId}/projects/${p.Rid}`); }}>
                        <IconExternalLink size={14} />
                      </ActionIcon>
                      <ActionIcon size="sm" variant="subtle" color="blue" title="Edit"
                        onClick={(e) => { e.stopPropagation(); openEdit(p); }}>
                        <IconEdit size={14} />
                      </ActionIcon>
                      <ActionIcon size="sm" variant="subtle" color="gray" title="Duplicate"
                        onClick={(e) => { e.stopPropagation(); openDuplicate(p); }}>
                        <IconCopy size={14} />
                      </ActionIcon>
                      <ActionIcon size="sm" variant="subtle" color="red" title="Delete"
                        onClick={(e) => { e.stopPropagation(); openDelete(p); }}>
                        <IconTrash size={14} />
                      </ActionIcon>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </div>
      </Paper>

      {/* ── Create / Edit Modal ──────────────────────────────────── */}
      <Modal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingProject ? 'Edit Project' : 'New Project'}
        size="xl"
        styles={{ body: { maxHeight: 'calc(100vh - 120px)', overflowY: 'auto' } }}
      >
        <Tabs value={activeTab} onChange={setActiveTab}>
          <Tabs.List>
            <Tabs.Tab value="basic">Basic Info</Tabs.Tab>
            <Tabs.Tab value="location">Location</Tabs.Tab>
            <Tabs.Tab value="scope">Scope</Tabs.Tab>
            <Tabs.Tab value="financial">Financial</Tabs.Tab>
            <Tabs.Tab value="mitigation">Mitigation</Tabs.Tab>
            <Tabs.Tab value="schedule">Schedule</Tabs.Tab>
            <Tabs.Tab value="certs">Certifications</Tabs.Tab>
            <Tabs.Tab value="notes">Notes</Tabs.Tab>
          </Tabs.List>

          {/* ── Basic Info ──────────────────────────────────────── */}
          <Tabs.Panel value="basic" pt="md">
            <SimpleGrid cols={2} spacing="sm">
              <TextInput label="Project Number" readOnly
                description="Auto-assigned"
                value={form.projectNumber}
                styles={{ input: { fontFamily: 'DM Mono, monospace', fontWeight: 600 } }} />
              <TextInput label="Project Title" required
                value={form.projectTitle} onChange={e => setField('projectTitle', e.currentTarget.value)} />
              <Select label="Category (A-G)" data={CATEGORY_OPTIONS} clearable
                value={form.category || null} onChange={v => setField('category', v ?? '')} />
              <Select label="Project Size" data={SIZE_OPTIONS} clearable
                value={form.projectSize || null} onChange={v => setField('projectSize', v ?? '')} />
              <Select label="Work Type" data={WORK_TYPE_OPTIONS} clearable searchable
                value={form.workType || null} onChange={v => setField('workType', v ?? '')} />
              <Select label="Organization" data={orgOptions} searchable clearable
                value={form.organizationId || null} onChange={v => setField('organizationId', v ?? '')} />
              <Select label="Disaster" data={disasterOptions} searchable clearable
                value={form.disasterId || null} onChange={v => setField('disasterId', v ?? '')} />
              <TextInput label="Assigned Specialist"
                value={form.assignedSpecialist} onChange={e => setField('assignedSpecialist', e.currentTarget.value)} />
            </SimpleGrid>
          </Tabs.Panel>

          {/* ── Location ────────────────────────────────────────── */}
          <Tabs.Panel value="location" pt="md">
            <SimpleGrid cols={2} spacing="sm">
              <div style={{ gridColumn: '1 / -1' }}>
                <Popover opened={addrOpen} onChange={setAddrOpen} position="bottom-start" width="target">
                  <Popover.Target>
                    <TextInput label="Address" placeholder="Start typing to search..."
                      description="Type 3+ characters to autocomplete — fills city, county, state, ZIP, and lat/lng"
                      value={form.projectAddress}
                      onChange={e => {
                        setField('projectAddress', e.currentTarget.value);
                        fetchAddressSuggestions(e.currentTarget.value);
                      }}
                      onFocus={() => addrSuggestions.length > 0 && setAddrOpen(true)}
                    />
                  </Popover.Target>
                  <Popover.Dropdown p={0}>
                    {addrSuggestions.map((s, i) => (
                      <div key={i}
                        style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #eee' }}
                        onMouseDown={() => pickAddress(s)}
                      >
                        <Text size="sm" fw={500}>{s.title}</Text>
                        {s.address?.label && s.address.label !== s.title && (
                          <Text size="xs" c="dimmed">{s.address.label}</Text>
                        )}
                      </div>
                    ))}
                  </Popover.Dropdown>
                </Popover>
              </div>
              <TextInput label="City"
                value={form.projectCity} onChange={e => setField('projectCity', e.currentTarget.value)} />
              <TextInput label="County"
                value={form.projectCounty} onChange={e => setField('projectCounty', e.currentTarget.value)} />
              <TextInput label="State"
                value={form.projectState} onChange={e => setField('projectState', e.currentTarget.value)} />
              <TextInput label="ZIP Code"
                value={form.projectZip} onChange={e => setField('projectZip', e.currentTarget.value)} />
              <TextInput label="Latitude"
                value={form.projectLat} onChange={e => setField('projectLat', e.currentTarget.value)} />
              <TextInput label="Longitude"
                value={form.projectLng} onChange={e => setField('projectLng', e.currentTarget.value)} />
            </SimpleGrid>
          </Tabs.Panel>

          {/* ── Scope ───────────────────────────────────────────── */}
          <Tabs.Panel value="scope" pt="md">
            <Stack gap="sm">
              <Textarea label="Damage Description & Dimensions (DDD)"
                description="Describe all damage including facility name, pre-disaster condition, cause of damage, and dimensions"
                autosize minRows={8} maxRows={30}
                value={form.damageDescription} onChange={e => setField('damageDescription', e.currentTarget.value)} />
              <Textarea label="Scope of Work (SOW)"
                description="Detailed scope including materials, methods, labor, equipment, and quantities"
                autosize minRows={8} maxRows={30}
                value={form.scopeOfWork} onChange={e => setField('scopeOfWork', e.currentTarget.value)} />
              <Textarea label="Cost Narrative"
                description="Explain and justify project costs"
                autosize minRows={4} maxRows={20}
                value={form.costNarrative} onChange={e => setField('costNarrative', e.currentTarget.value)} />
              {/* Repair/Replace only applies to permanent work (Cat C-G), not Cat A/B */}
              {form.category && !['A', 'B'].includes(form.category) && (
                <>
                  <SimpleGrid cols={2} spacing="sm">
                    <Select label="Repair or Replace" data={REPAIR_REPLACE_OPTIONS} clearable
                      value={form.repairOrReplace || null} onChange={v => setField('repairOrReplace', v ?? '')} />
                    <Select label="Improved Project Election" data={YES_NO_OPTIONS}
                      value={form.improvedProjectElection || 'no'}
                      onChange={v => setField('improvedProjectElection', v ?? 'no')} />
                  </SimpleGrid>
                  {form.improvedProjectElection === 'yes' && (
                    <Textarea label="Improved Project Description" rows={4}
                      value={form.improvedProjectDescription}
                      onChange={e => setField('improvedProjectDescription', e.currentTarget.value)} />
                  )}
                </>
              )}
            </Stack>
          </Tabs.Panel>

          {/* ── Financial ───────────────────────────────────────── */}
          <Tabs.Panel value="financial" pt="md">
            <SimpleGrid cols={2} spacing="sm">
              <TextInput label="Estimated Cost"
                placeholder="$0"
                value={formatCurrency(form.estimatedCost)}
                onChange={e => setField('estimatedCost', stripCurrency(e.currentTarget.value))}
                styles={{ input: { fontFamily: 'DM Mono, monospace' } }} />
              <TextInput label="Actual Cost"
                placeholder="$0"
                value={formatCurrency(form.actualCost)}
                onChange={e => setField('actualCost', stripCurrency(e.currentTarget.value))}
                styles={{ input: { fontFamily: 'DM Mono, monospace' } }} />
              <TextInput label="Federal Share %"
                value={form.federalSharePct} onChange={e => setField('federalSharePct', e.currentTarget.value)}
                styles={{ input: { fontFamily: 'DM Mono, monospace' } }} />
              <TextInput label="Federal Share Amount"
                placeholder="$0"
                value={formatCurrency(form.federalShareAmount)}
                onChange={e => setField('federalShareAmount', stripCurrency(e.currentTarget.value))}
                styles={{ input: { fontFamily: 'DM Mono, monospace' } }} />
              <TextInput label="Applicant Share Amount"
                placeholder="$0"
                value={formatCurrency(form.applicantShareAmount)}
                onChange={e => setField('applicantShareAmount', stripCurrency(e.currentTarget.value))}
                styles={{ input: { fontFamily: 'DM Mono, monospace' } }} />
              <TextInput label="Insurance Deduction"
                placeholder="$0"
                value={formatCurrency(form.insuranceDeduction)}
                onChange={e => setField('insuranceDeduction', stripCurrency(e.currentTarget.value))}
                styles={{ input: { fontFamily: 'DM Mono, monospace' } }} />
              <TextInput label="Net Eligible Cost"
                placeholder="$0"
                value={formatCurrency(form.netEligibleCost)}
                onChange={e => setField('netEligibleCost', stripCurrency(e.currentTarget.value))}
                styles={{ input: { fontFamily: 'DM Mono, monospace' } }} />
              <TextInput label="Obligated Amount"
                placeholder="$0"
                value={formatCurrency(form.obligatedAmount)}
                onChange={e => setField('obligatedAmount', stripCurrency(e.currentTarget.value))}
                styles={{ input: { fontFamily: 'DM Mono, monospace' } }} />
              <TextInput label="Drawdown Amount"
                placeholder="$0"
                value={formatCurrency(form.drawdownAmount)}
                onChange={e => setField('drawdownAmount', stripCurrency(e.currentTarget.value))}
                styles={{ input: { fontFamily: 'DM Mono, monospace' } }} />
              <TextInput label="Pending Reimbursement"
                placeholder="$0"
                value={formatCurrency(form.pendingReimbursement)}
                onChange={e => setField('pendingReimbursement', stripCurrency(e.currentTarget.value))}
                styles={{ input: { fontFamily: 'DM Mono, monospace' } }} />
            </SimpleGrid>
          </Tabs.Panel>

          {/* ── Mitigation ──────────────────────────────────────── */}
          <Tabs.Panel value="mitigation" pt="md">
            <Stack gap="sm">
              <SimpleGrid cols={2} spacing="sm">
                <Select label="Hazard Mitigation Included" data={YES_NO_OPTIONS}
                  value={form.hazardMitigationIncluded || 'no'}
                  onChange={v => setField('hazardMitigationIncluded', v ?? 'no')} />
                <TextInput label="Hazard Mitigation Cost"
                  placeholder="$0"
                  value={formatCurrency(form.hazardMitigationCost)}
                  onChange={e => setField('hazardMitigationCost', stripCurrency(e.currentTarget.value))}
                  styles={{ input: { fontFamily: 'DM Mono, monospace' } }} />
              </SimpleGrid>
              <Textarea label="Hazard Mitigation Description" rows={4}
                value={form.hazardMitigationDescription}
                onChange={e => setField('hazardMitigationDescription', e.currentTarget.value)} />
              <TextInput label="Benefit-Cost Analysis (BCA)"
                value={form.hazardMitigationBca}
                onChange={e => setField('hazardMitigationBca', e.currentTarget.value)} />
            </Stack>
          </Tabs.Panel>

          {/* ── Schedule ────────────────────────────────────────── */}
          <Tabs.Panel value="schedule" pt="md">
            <SimpleGrid cols={2} spacing="sm">
              <Select label="Phase" data={PHASE_OPTIONS}
                value={form.phase || 'formulation'}
                onChange={v => setField('phase', v ?? 'formulation')} />
              <Select label="Status" data={STATUS_OPTIONS}
                value={form.status || 'draft'}
                onChange={v => setField('status', v ?? 'draft')} />
              <TextInput label="Work Start Date" placeholder="YYYY-MM-DD"
                value={form.workStartDate} onChange={e => setField('workStartDate', e.currentTarget.value)} />
              <TextInput label="Deadline" placeholder="YYYY-MM-DD"
                value={form.deadline} onChange={e => setField('deadline', e.currentTarget.value)} />
              <TextInput label="Completion Date" placeholder="YYYY-MM-DD"
                value={form.completionDate} onChange={e => setField('completionDate', e.currentTarget.value)} />
              <TextInput label="Completion % Estimate"
                value={form.completionPctEstimate}
                onChange={e => setField('completionPctEstimate', e.currentTarget.value)} />
              <Select label="Extension Requested" data={YES_NO_OPTIONS}
                value={form.extensionRequested || 'no'}
                onChange={v => setField('extensionRequested', v ?? 'no')} />
              {form.extensionRequested === 'yes' && (
                <TextInput label="Extension New Deadline" placeholder="YYYY-MM-DD"
                  value={form.extensionNewDeadline}
                  onChange={e => setField('extensionNewDeadline', e.currentTarget.value)} />
              )}
            </SimpleGrid>
            {form.extensionRequested === 'yes' && (
              <Textarea label="Extension Justification" rows={3} mt="sm"
                value={form.extensionJustification}
                onChange={e => setField('extensionJustification', e.currentTarget.value)} />
            )}
          </Tabs.Panel>

          {/* ── Certifications ──────────────────────────────────── */}
          <Tabs.Panel value="certs" pt="md">
            <Stack gap="sm">
              <SimpleGrid cols={2} spacing="sm">
                <Select label="CEF Required" data={YES_NO_OPTIONS}
                  value={form.cefRequired || 'no'}
                  onChange={v => setField('cefRequired', v ?? 'no')} />
                <Select label="Pre-Approval Required" data={YES_NO_OPTIONS}
                  value={form.preApprovalRequired || 'no'}
                  onChange={v => setField('preApprovalRequired', v ?? 'no')} />
                {form.preApprovalRequired === 'yes' && (
                  <TextInput label="Pre-Approval Status"
                    value={form.preApprovalStatus}
                    onChange={e => setField('preApprovalStatus', e.currentTarget.value)} />
                )}
              </SimpleGrid>
              <Text size="sm" fw={600} c="dimmed" mt="sm">Applicant Certifications</Text>
              <Checkbox
                label="Legal Responsibility — Applicant is legally responsible for the facility/site"
                checked={form.certLegalResponsibility === 'yes'}
                onChange={e => setField('certLegalResponsibility', e.currentTarget.checked ? 'yes' : 'no')}
              />
              <Checkbox
                label="Insurance Compliance — Applicant has obtained/maintained required insurance"
                checked={form.certInsuranceCompliance === 'yes'}
                onChange={e => setField('certInsuranceCompliance', e.currentTarget.checked ? 'yes' : 'no')}
              />
              <Checkbox
                label="Procurement Compliance — Procurement followed 2 CFR 200 requirements"
                checked={form.certProcurementCompliance === 'yes'}
                onChange={e => setField('certProcurementCompliance', e.currentTarget.checked ? 'yes' : 'no')}
              />
              <Checkbox
                label="No Duplication of Work — Work is not duplicated by other funding sources"
                checked={form.certNoWorkDuplication === 'yes'}
                onChange={e => setField('certNoWorkDuplication', e.currentTarget.checked ? 'yes' : 'no')}
              />
              <SimpleGrid cols={2} spacing="sm" mt="sm">
                <TextInput label="Certified / Signed By"
                  value={form.certSignedBy} onChange={e => setField('certSignedBy', e.currentTarget.value)} />
                <TextInput label="Certification Date" type="date"
                  value={form.certSignedDate} onChange={e => setField('certSignedDate', e.currentTarget.value)} />
              </SimpleGrid>
            </Stack>
          </Tabs.Panel>

          {/* ── Notes ───────────────────────────────────────────── */}
          <Tabs.Panel value="notes" pt="md">
            <Textarea label="Notes" rows={8}
              value={form.notes} onChange={e => setField('notes', e.currentTarget.value)} />
          </Tabs.Panel>
        </Tabs>

        <Group justify="flex-end" mt="lg">
          <Button variant="default" onClick={() => setModalOpen(false)}>Cancel</Button>
          <Button loading={saving} onClick={handleSave}>
            {editingProject ? 'Save Changes' : 'Create Project'}
          </Button>
        </Group>
      </Modal>

      {/* ── Delete confirmation ─────────────────────────────────── */}
      <Modal
        opened={deleteOpen}
        onClose={() => { setDeleteOpen(false); setDeletingProject(null); }}
        title="Delete Project"
        centered
        size="sm"
      >
        <Stack>
          <Text size="sm">
            Delete project <strong>{deletingProject?.projectNumber}</strong>{' '}
            &mdash; {deletingProject?.projectTitle}? This cannot be undone.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" size="xs"
              onClick={() => { setDeleteOpen(false); setDeletingProject(null); }}>Cancel</Button>
            <Button color="red" size="xs" loading={deleting} onClick={handleDelete}>Delete</Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
